/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/outboundRouteApi.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    API functions for Outbound Routes.
    Centralizes all API calls related to Outbound Routes.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  OutboundRoute,
  OutboundRoutePayload,
  ListOutboundRoutesResponse,
  OutboundRouteResponse
} from '../types';

export const OUTBOUND_ROUTES_ENDPOINT = '/api/telephony/outbound-routes';

/**
 * Generic API request helper
 */
async function requestOutboundRoutesApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {})
    }
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List outbound routes with pagination and search
 */
export async function fetchOutboundRoutes(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string
): Promise<ListOutboundRoutesResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${OUTBOUND_ROUTES_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch outbound routes');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as OutboundRoute[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0
    }
  };
}

/**
 * Get single outbound route by ID
 */
export async function fetchOutboundRoute(id: string | number): Promise<OutboundRoute> {
  const { data } = await requestOutboundRoutesApi<OutboundRoute>(
    `${OUTBOUND_ROUTES_ENDPOINT}/${id}`,
    'Failed to fetch outbound route'
  );
  return data;
}

/**
 * Create new outbound route
 */
export async function createOutboundRoute(
  payload: OutboundRoutePayload
): Promise<OutboundRoute> {
  const { data } = await requestOutboundRoutesApi<OutboundRoute>(
    OUTBOUND_ROUTES_ENDPOINT,
    'Failed to create outbound route',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return data;
}

/**
 * Update outbound route
 */
export async function updateOutboundRoute(
  id: string | number,
  payload: OutboundRoutePayload
): Promise<OutboundRoute> {
  const { data } = await requestOutboundRoutesApi<OutboundRoute>(
    `${OUTBOUND_ROUTES_ENDPOINT}/${id}`,
    'Failed to update outbound route',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  return data;
}

/**
 * Delete outbound route
 */
export async function deleteOutboundRoute(id: string | number): Promise<void> {
  await requestOutboundRoutesApi<null>(
    `${OUTBOUND_ROUTES_ENDPOINT}/${id}`,
    'Failed to delete outbound route',
    {
      method: 'DELETE'
    }
  );
}

